import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Alert, Button } from '../../components/ui'
import { createProperty } from '../../api/objects-write'
import { t } from '../../i18n'

/**
 * Entering an address, and nothing else.
 *
 * Three fields, one of them required. An object is recorded while somebody is on
 * the phone or standing in a hallway, and the shape of the place is not known
 * yet: how many rooms, whether one of them holds two places, what the bills come
 * to. A form that asked for all of it now would be filled in with guesses, and a
 * guess is worse than a gap — unknown blocks "within budget" and an invented
 * zero is quoted to somebody.
 *
 * Everything else is added afterwards from the card, where the person is looking
 * at the place rather than at a form.
 */
export function NewPropertyForm({ onDone }: { readonly onDone?: () => void }) {
  const [open, setOpen] = useState(false)
  const [address, setAddress] = useState('')
  const [area, setArea] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()
  const navigate = useNavigate()

  const create = useMutation({
    mutationFn: () => createProperty({ address_normalized: address.trim(), area: area.trim() }),
    onSuccess: ({ id }) => {
      setAddress('')
      setArea('')
      setFailure(null)
      setOpen(false)
      void client.invalidateQueries({ queryKey: ['properties'] })
      onDone?.()
      // Straight into the card, because the next thing anybody does is add the
      // rooms.
      void navigate(`/objects/${id}`)
    },
    onError: (error: Error) => setFailure(error.message),
  })

  if (!open) {
    return (
      <Button variant="primary" block onClick={() => setOpen(true)}>
        {t('build.newProperty')}
      </Button>
    )
  }

  const ready = address.trim() !== ''

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready) create.mutate()
      }}
    >
      <p className="build-form__hint">{t('build.newPropertyHint')}</p>

      <div className="field">
        <label className="field__label" htmlFor="new-property-address">
          {t('build.address')}
        </label>
        <input
          id="new-property-address"
          className="field__input"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder={t('build.addressPlaceholder')}
          autoFocus
          required
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="new-property-area">
          {t('build.area')}
        </label>
        <input
          id="new-property-area"
          className="field__input"
          value={area}
          onChange={(event) => setArea(event.target.value)}
          placeholder={t('build.areaPlaceholder')}
        />
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={create.isPending} disabled={!ready}>
          {create.isPending ? t('build.creating') : t('build.create')}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)} disabled={create.isPending}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
